import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  productionRunSchema,
  RECIPE_KIND_LABELS,
  recipeSchema,
  voidReasonSchema,
  type ProductDTO,
  type ProductionRunDTO,
  type RecipeDTO,
  type RecipeKind,
  type RecipeLineRole,
  type UnitType,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '@bifeai/ui';
import { ChefHat, CookingPot, History, Pencil, Plus, Scissors, Trash2 } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useProducts } from '../../lib/catalog';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import { centsToPesosInput, formatQty, fromInternalQty, pesosToCents, priceUnit, todayIso, toInternalQty } from '../../lib/units';

type Tab = 'produce' | 'recipes' | 'history';

function useRecipes() {
  return useQuery({ queryKey: ['production', 'recipes'], queryFn: () => api.get<RecipeDTO[]>('/production/recipes') });
}

const pct = (r: number | null) => (r === null ? '—' : `${Math.round(r * 100)} %`);

/**
 * Producción: elaborados con receta (milanesas, hamburguesas, chorizos) y limpieza de piezas
 * (vacío entero → vacío limpio + grasa). El costo sale de lo que realmente se usó.
 */
export function ProductionPage() {
  const [tab, setTab] = useState<Tab>('produce');
  const recipes = useRecipes();
  const [editing, setEditing] = useState<RecipeDTO | 'new-PRODUCTION' | 'new-CLEANING' | null>(null);
  const [producing, setProducing] = useState<RecipeDTO | null>(null);
  const [result, setResult] = useState<ProductionRunDTO | null>(null);
  const list = recipes.data ?? [];

  return (
    <>
      <PageHeader
        title="Producción"
        subtitle="Milanesas, hamburguesas, chorizos… Cargás la receta una vez; al producir ponés lo que usaste y lo que salió. Lo que se saca (grasa, recorte) va al tacho."
        actions={
          <Button variant="primary" icon={<ChefHat />} onClick={() => setEditing('new-PRODUCTION')}>
            Nueva receta
          </Button>
        }
      />
      <div className="bf-tabs" role="tablist">
        {(
          [
            ['produce', 'Producir'],
            ['recipes', 'Recetas'],
            ['history', 'Historial'],
          ] as const
        ).map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'history' ? (
        <RunsHistory />
      ) : recipes.isPending ? (
        <Skeleton style={{ height: 200 }} />
      ) : list.length === 0 ? (
        <Card>
          <EmptyState
            icon={<CookingPot />}
            title="Todavía no hay recetas"
            text="Cargá una receta (ej.: milanesas de nalga) o una limpieza (ej.: vacío entero → vacío limpio + grasa). Se carga una vez y después se usa cada vez que producís."
            action={
              <Button variant="primary" icon={<Plus />} onClick={() => setEditing('new-PRODUCTION')}>
                Cargar la primera
              </Button>
            }
          />
        </Card>
      ) : tab === 'produce' ? (
        <div className="bf-recipe-grid">
          {list.map((r) => (
            <button key={r.id} type="button" className="bf-recipe-card" onClick={() => setProducing(r)}>
              <Badge tone={r.kind === 'CLEANING' ? 'warning' : 'info'}>{RECIPE_KIND_LABELS[r.kind]}</Badge>
              <strong>{r.name}</strong>
              <span className="bf-cell-sub">
                Rinde {formatQty(r.outputUnitType, r.outputQty)} de {r.outputProductName}
              </span>
              {r.estimatedUnitCostCents !== null && (
                <span className="bf-cell-sub">
                  Costo estimado: <strong className="bf-num">{formatMoney(r.estimatedUnitCostCents)}</strong>
                  {priceUnit(r.outputUnitType)}
                </span>
              )}
              <span className="bf-recipe-go">{r.kind === 'CLEANING' ? 'Limpiar' : 'Producir'} →</span>
            </button>
          ))}
        </div>
      ) : (
        <Card>
          <CardBody flush>
            <table className="bf-table">
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <span className="bf-cell-main">{r.name}</span>{' '}
                      <Badge tone={r.kind === 'CLEANING' ? 'warning' : 'info'}>{RECIPE_KIND_LABELS[r.kind]}</Badge>
                      <div className="bf-cell-sub">
                        {r.lines
                          .map((l) =>
                            l.role === 'FIXED_COST'
                              ? `${l.label} ${formatMoney(l.fixedCostCents ?? 0)}`
                              : `${l.productName} ${formatQty(l.unitType ?? 'WEIGHT', l.qty)}`,
                          )
                          .join(' · ')}
                      </div>
                    </td>
                    <td className="bf-cell-sub">{r.lastRunAt ? `Última vez: ${formatDate(r.lastRunAt)}` : 'Sin usar'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Button size="sm" variant="ghost" icon={<Pencil />} onClick={() => setEditing(r)}>
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      {editing && (
        <RecipeDialog
          recipe={typeof editing === 'string' ? null : editing}
          kind={typeof editing === 'string' ? (editing.slice(4) as RecipeKind) : editing.kind}
          onClose={() => setEditing(null)}
        />
      )}
      {producing && (
        <ProduceDialog
          recipe={producing}
          onClose={() => setProducing(null)}
          onDone={(run) => {
            setProducing(null);
            setResult(run);
          }}
        />
      )}
      {result && <RunDialog run={result} onClose={() => setResult(null)} justCreated />}
    </>
  );
}

// ---------------------------------------------------------------- editor de recetas
interface LineState {
  role: RecipeLineRole;
  productId: string;
  qty: string;
  fixed: string;
  label: string;
}

function ProductSelect({
  value,
  onChange,
  products,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  products: ProductDTO[];
  placeholder: string;
}) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} aria-label={placeholder}>
      <option value="">{placeholder}</option>
      {products.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name}
          {p.kind === 'SUPPLY' ? ' (insumo)' : ''}
        </option>
      ))}
    </Select>
  );
}

function RecipeDialog({ recipe, kind, onClose }: { recipe: RecipeDTO | null; kind: RecipeKind; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const products = useProducts();
  const all = useMemo(() => (products.data ?? []).filter((p) => p.isActive), [products.data]);
  const unitOf = (id: string): UnitType => all.find((p) => p.id === id)?.unitType ?? 'WEIGHT';
  const cleaning = kind === 'CLEANING';
  const [name, setName] = useState(recipe?.name ?? '');
  const [outputProductId, setOutput] = useState(recipe?.outputProductId ?? '');
  const [outputQty, setOutputQty] = useState(recipe ? fromInternalQty(recipe.outputUnitType, recipe.outputQty) : '');
  const [shelf, setShelf] = useState(recipe?.shelfLifeDays?.toString() ?? '');
  const [notes, setNotes] = useState(recipe?.notes ?? '');
  const [lines, setLines] = useState<LineState[]>(
    recipe?.lines.map((l) => ({
      role: l.role,
      productId: l.productId ?? '',
      qty: l.unitType ? fromInternalQty(l.unitType, l.qty) : '',
      fixed: centsToPesosInput(l.fixedCostCents),
      label: l.label ?? '',
    })) ??
      (cleaning
        ? [
            { role: 'INPUT', productId: '', qty: '', fixed: '', label: '' },
            { role: 'BYPRODUCT', productId: '', qty: '', fixed: '', label: '' },
          ]
        : [{ role: 'INPUT', productId: '', qty: '', fixed: '', label: '' }]),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const setLine = (i: number, patch: Partial<LineState>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const add = (role: RecipeLineRole) =>
    setLines((ls) => [...ls, { role, productId: '', qty: '', fixed: '', label: role === 'FIXED_COST' ? 'Sal y condimentos' : '' }]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = recipeSchema.safeParse({
      kind,
      name,
      outputProductId,
      outputQty: outputProductId ? (toInternalQty(unitOf(outputProductId), outputQty || '0') ?? 0) : 0,
      shelfLifeDays: shelf ? Number(shelf) : null,
      notes,
      lines: lines
        .filter((l) => l.productId || l.role === 'FIXED_COST')
        .map((l) =>
          l.role === 'FIXED_COST'
            ? { role: l.role, label: l.label, fixedCostCents: pesosToCents(l.fixed || '0') ?? 0 }
            : { role: l.role, productId: l.productId, qty: toInternalQty(unitOf(l.productId), l.qty || '0') ?? 0 },
        ),
    });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Revisá los datos.');
    setBusy(true);
    setError(null);
    try {
      if (recipe) await api.put(`/production/recipes/${recipe.id}`, parsed.data);
      else await api.post('/production/recipes', parsed.data);
      await qc.invalidateQueries({ queryKey: ['production'] });
      toast.success(recipe ? 'Receta guardada' : 'Receta creada');
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function archive() {
    if (!recipe) return;
    setBusy(true);
    try {
      await api.post(`/production/recipes/${recipe.id}/archive`, { archived: true });
      await qc.invalidateQueries({ queryKey: ['production'] });
      toast.success('Receta archivada');
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const qtyLabel = (id: string) => (id && unitOf(id) === 'UNIT' ? 'u.' : 'kg');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={recipe ? `Editar: ${recipe.name}` : cleaning ? 'Nueva limpieza de pieza' : 'Nueva receta'}
      description={
        cleaning
          ? 'Una pieza que se limpia: lo que entra (ej.: vacío entero), lo que queda para vender (vacío limpio) y los subproductos que también se venden (grasa). Lo que falta es merma.'
          : 'Las cantidades son para una tanda. Al producir se ajustan con lo que usaste de verdad.'
      }
      wide
    >
      <form className="bf-form" onSubmit={submit} noValidate>
        {error && <Callout tone="danger">{error}</Callout>}
        <Field label="Nombre">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            placeholder={cleaning ? 'Limpieza de vacío' : 'Milanesas de nalga'}
          />
        </Field>

        <div className="bf-recipe-section">
          <h3>{cleaning ? 'Pieza que entra' : 'Ingredientes'}</h3>
          {lines.map((l, i) =>
            l.role === 'INPUT' ? (
              <div key={i} className="bf-recipe-line">
                <ProductSelect
                  value={l.productId}
                  onChange={(v) => setLine(i, { productId: v })}
                  products={all}
                  placeholder={cleaning ? 'Elegí la pieza' : 'Elegí el ingrediente'}
                />
                <Input
                  value={l.qty}
                  onChange={(e) => setLine(i, { qty: e.target.value })}
                  inputMode="decimal"
                  placeholder={qtyLabel(l.productId)}
                  aria-label="Cantidad"
                />
                <span className="bf-cell-sub">{qtyLabel(l.productId)}</span>
                {!cleaning && (
                  <button type="button" className="bf-icon-btn" aria-label="Quitar" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>
                    <Trash2 />
                  </button>
                )}
              </div>
            ) : null,
          )}
          {!cleaning && (
            <Button size="sm" variant="ghost" icon={<Plus />} onClick={() => add('INPUT')}>
              Ingrediente o insumo
            </Button>
          )}
        </div>

        {!cleaning && (
          <div className="bf-recipe-section">
            <h3>Costos fijos por tanda</h3>
            <p className="bf-cell-sub">
              Para lo que no querés contar en gramos (sal, condimentos, gas). Si preferís controlarlo con stock, cargalo como insumo arriba.
            </p>
            {lines.map((l, i) =>
              l.role === 'FIXED_COST' ? (
                <div key={i} className="bf-recipe-line">
                  <Input
                    value={l.label}
                    onChange={(e) => setLine(i, { label: e.target.value })}
                    placeholder="Sal y condimentos"
                    aria-label="Concepto"
                  />
                  <Input
                    value={l.fixed}
                    onChange={(e) => setLine(i, { fixed: e.target.value })}
                    inputMode="decimal"
                    placeholder="$ 0"
                    aria-label="Costo"
                  />
                  <span className="bf-cell-sub">$</span>
                  <button type="button" className="bf-icon-btn" aria-label="Quitar" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>
                    <Trash2 />
                  </button>
                </div>
              ) : null,
            )}
            <Button size="sm" variant="ghost" icon={<Plus />} onClick={() => add('FIXED_COST')}>
              Costo fijo
            </Button>
          </div>
        )}

        <div className="bf-recipe-section">
          <h3>{cleaning ? 'Queda para vender' : 'Producto terminado'}</h3>
          <div className="bf-recipe-line">
            <ProductSelect
              value={outputProductId}
              onChange={setOutput}
              products={all.filter((p) => p.kind !== 'SUPPLY')}
              placeholder={cleaning ? 'Ej.: vacío limpio' : 'Ej.: milanesa de nalga'}
            />
            <Input
              value={outputQty}
              onChange={(e) => setOutputQty(e.target.value)}
              inputMode="decimal"
              placeholder={qtyLabel(outputProductId)}
              aria-label="Rinde"
            />
            <span className="bf-cell-sub">{qtyLabel(outputProductId)}</span>
          </div>
        </div>

        {cleaning && (
          <div className="bf-recipe-section">
            <h3>Subproductos que se venden {cleaning ? '' : '(opcional)'}</h3>
            <p className="bf-cell-sub">Ej.: grasa, hueso. Entran al stock y lo que valen baja el costo del producto principal.</p>
            {lines.map((l, i) =>
              l.role === 'BYPRODUCT' ? (
                <div key={i} className="bf-recipe-line">
                  <ProductSelect value={l.productId} onChange={(v) => setLine(i, { productId: v })} products={all} placeholder="Ej.: grasa" />
                  <Input
                    value={l.qty}
                    onChange={(e) => setLine(i, { qty: e.target.value })}
                    inputMode="decimal"
                    placeholder={qtyLabel(l.productId)}
                    aria-label="Cantidad"
                  />
                  <span className="bf-cell-sub">{qtyLabel(l.productId)}</span>
                  <button type="button" className="bf-icon-btn" aria-label="Quitar" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>
                    <Trash2 />
                  </button>
                </div>
              ) : null,
            )}
            <Button size="sm" variant="ghost" icon={<Plus />} onClick={() => add('BYPRODUCT')}>
              Subproducto
            </Button>
          </div>
        )}

        <div className="bf-form-row">
          <Field label="Vence a los (días)" hint="Vacío = sin vencimiento.">
            <Input value={shelf} onChange={(e) => setShelf(e.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="Ej.: 3" />
          </Field>
          <Field label="Notas">
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>
        <div className="bf-form-actions">
          {recipe && (
            <Button variant="ghost" onClick={() => void archive()} disabled={busy} style={{ marginRight: 'auto' }}>
              Archivar
            </Button>
          )}
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            Guardar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------- producir
interface RunLine {
  role: RecipeLineRole;
  productId: string | null;
  productName: string | null;
  unitType: UnitType;
  base: number;
  qty: string;
  fixed: string;
  label: string | null;
  value: string;
  openPrice: boolean;
}

function ProduceDialog({ recipe, onClose, onDone }: { recipe: RecipeDTO; onClose: () => void; onDone: (r: ProductionRunDTO) => void }) {
  const qc = useQueryClient();
  const { me, multiBranch, isHeadquartersUser, selectedBranchId } = useSession();
  const cleaning = recipe.kind === 'CLEANING';
  const [factor, setFactor] = useState('1');
  const [lines, setLines] = useState<RunLine[]>(() =>
    recipe.lines.map((l) => ({
      role: l.role,
      productId: l.productId,
      productName: l.productName,
      unitType: l.unitType ?? 'WEIGHT',
      base: l.qty,
      qty: l.unitType ? fromInternalQty(l.unitType, l.qty) : '',
      fixed: centsToPesosInput(l.fixedCostCents),
      label: l.label,
      value: centsToPesosInput(l.suggestedValueCents),
      openPrice: l.openPrice,
    })),
  );
  const [output, setOutput] = useState(fromInternalQty(recipe.outputUnitType, recipe.outputQty));
  const [expires, setExpires] = useState('');
  const [notes, setNotes] = useState('');
  const [loss, setLoss] = useState('');
  const [branchId, setBranchId] = useState(selectedBranchId ?? me.membership.branchId ?? me.branches.find((b) => b.isHeadquarters)?.id ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const f = Number(factor.replace(',', '.')) || 0;
  const expected = Math.round(recipe.outputQty * f);

  function scale(v: string) {
    setFactor(v);
    const k = Number(v.replace(',', '.')) || 0;
    if (k <= 0) return;
    setLines((ls) => ls.map((l) => ({ ...l, qty: l.role === 'FIXED_COST' ? l.qty : fromInternalQty(l.unitType, Math.round(l.base * k)) })));
    setLines((ls) =>
      ls.map((l) =>
        l.role === 'FIXED_COST'
          ? { ...l, fixed: centsToPesosInput(Math.round((recipe.lines.find((x) => x.label === l.label)?.fixedCostCents ?? 0) * k)) }
          : l,
      ),
    );
    setOutput(fromInternalQty(recipe.outputUnitType, Math.round(recipe.outputQty * k)));
  }
  const setLine = (i: number, patch: Partial<RunLine>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = productionRunSchema.safeParse({
      kind: recipe.kind,
      recipeId: recipe.id,
      branchId: multiBranch && isHeadquartersUser ? branchId : null,
      outputProductId: recipe.outputProductId,
      outputQty: toInternalQty(recipe.outputUnitType, output || '0') ?? 0,
      expectedQty: expected > 0 ? expected : null,
      expiresAt: expires || null,
      lossQty: loss.trim() ? toInternalQty('WEIGHT', loss) : null,
      notes,
      lines: lines.map((l) =>
        l.role === 'FIXED_COST'
          ? { role: l.role, label: l.label, fixedCostCents: pesosToCents(l.fixed || '0') ?? 0 }
          : {
              role: l.role,
              productId: l.productId,
              qty: toInternalQty(l.unitType, l.qty || '0') ?? 0,
              ...(l.role === 'BYPRODUCT' ? { unitValueCents: l.value.trim() ? pesosToCents(l.value) : null } : {}),
            },
      ),
    });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Revisá los datos.');
    setBusy(true);
    setError(null);
    try {
      const run = await api.post<ProductionRunDTO>('/production/runs', parsed.data);
      await Promise.all(['production', 'stock', 'products', 'pos', 'dashboard', 'notifications'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      onDone(run);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const unit = (t: UnitType) => (t === 'UNIT' ? 'u.' : 'kg');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={recipe.name}
      description="Corregí con lo que usaste y lo que salió de verdad: el costo se calcula con eso."
      wide
    >
      <form className="bf-form" onSubmit={submit} noValidate>
        {error && <Callout tone="danger">{error}</Callout>}
        {!cleaning && (
          <Field label="Tandas" hint="1 = la receta tal cual; 2 = el doble; 0,5 = la mitad.">
            <Input value={factor} onChange={(e) => scale(e.target.value)} inputMode="decimal" style={{ maxWidth: 120 }} />
          </Field>
        )}
        <div className="bf-recipe-section">
          <h3>{cleaning ? 'Pieza' : 'Lo que usaste'}</h3>
          {lines.map((l, i) =>
            l.role === 'INPUT' ? (
              <div key={i} className="bf-recipe-line">
                <span className="bf-recipe-name">{l.productName}</span>
                <Input
                  value={l.qty}
                  onChange={(e) => setLine(i, { qty: e.target.value })}
                  inputMode="decimal"
                  aria-label={`Cantidad de ${l.productName}`}
                />
                <span className="bf-cell-sub">{unit(l.unitType)}</span>
              </div>
            ) : l.role === 'FIXED_COST' ? (
              <div key={i} className="bf-recipe-line">
                <span className="bf-recipe-name">{l.label}</span>
                <Input
                  value={l.fixed}
                  onChange={(e) => setLine(i, { fixed: e.target.value })}
                  inputMode="decimal"
                  aria-label={`Costo de ${l.label}`}
                />
                <span className="bf-cell-sub">$</span>
              </div>
            ) : null,
          )}
        </div>
        <div className="bf-recipe-section">
          <h3>Lo que salió</h3>
          <div className="bf-recipe-line">
            <span className="bf-recipe-name">
              <strong>{recipe.outputProductName}</strong>
            </span>
            <Input
              className="bf-input--lg"
              value={output}
              onChange={(e) => setOutput(e.target.value)}
              inputMode="decimal"
              aria-label="Cantidad producida"
            />
            <span className="bf-cell-sub">{unit(recipe.outputUnitType)}</span>
          </div>
          {lines.map((l, i) =>
            l.role === 'BYPRODUCT' ? (
              <div key={i} className="bf-recipe-line bf-recipe-line--by">
                <span className="bf-recipe-name">{l.productName}</span>
                <Input
                  value={l.qty}
                  onChange={(e) => setLine(i, { qty: e.target.value })}
                  inputMode="decimal"
                  aria-label={`Cantidad de ${l.productName}`}
                />
                <span className="bf-cell-sub">{unit(l.unitType)}</span>
                <Input
                  value={l.value}
                  onChange={(e) => setLine(i, { value: e.target.value })}
                  inputMode="decimal"
                  placeholder={l.openPrice ? 'Precio hoy' : '$'}
                  aria-label={`Valor por ${unit(l.unitType)} de ${l.productName}`}
                />
                <span className="bf-cell-sub">{priceUnit(l.unitType)}</span>
              </div>
            ) : null,
          )}
          <div className="bf-recipe-line">
            <span className="bf-recipe-name">Lo que sacaste (grasa, recorte) · opcional</span>
            <Input value={loss} onChange={(e) => setLoss(e.target.value)} inputMode="decimal" placeholder="kg" aria-label="Recorte al tacho" />
            <span className="bf-cell-sub">kg</span>
          </div>
          <p className="bf-cell-sub">Si lo pesaste, anotalo: va al tacho y sirve para ver cuánto se saca. Ya está dentro del costo.</p>
          {lines.some((l) => l.role === 'BYPRODUCT') && (
            <p className="bf-cell-sub">
              Valor de los subproductos: lo que te paga el comprador (ej.: el sebero) o el valor que tengas fijo para el mes. Ese valor baja el costo
              de lo principal.
            </p>
          )}
        </div>
        <div className="bf-form-row">
          <Field
            label="Vence el"
            hint={recipe.shelfLifeDays ? `Vacío = en ${recipe.shelfLifeDays} días.` : 'Vacío = según Configuración → Vencimientos.'}
          >
            <Input type="date" value={expires} min={todayIso()} onChange={(e) => setExpires(e.target.value)} />
          </Field>
          {multiBranch && isHeadquartersUser && (
            <Field label="Sucursal">
              <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                {me.branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>
        <Field label="Notas (opcional)">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" icon={cleaning ? <Scissors /> : <CookingPot />} loading={busy}>
            {cleaning ? 'Registrar limpieza' : 'Registrar producción'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------- historial
function RunsHistory() {
  const [from, setFrom] = useState(todayIso().slice(0, 8) + '01');
  const [to, setTo] = useState(todayIso());
  const [open, setOpen] = useState<ProductionRunDTO | null>(null);
  const runs = useQuery({
    queryKey: ['production', 'runs', from, to],
    queryFn: () => api.get<ProductionRunDTO[]>(`/production/runs?from=${from}&to=${to}`),
    placeholderData: keepPreviousData,
  });
  const rows = runs.data ?? [];
  return (
    <>
      <div className="bf-period">
        <label className="bf-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Desde <Input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} />
        </label>
        <label className="bf-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Hasta <Input type="date" value={to} min={from} max={todayIso()} onChange={(e) => e.target.value && setTo(e.target.value)} />
        </label>
      </div>
      <Card>
        <CardBody flush>
          {runs.isPending ? (
            <div style={{ padding: 16 }}>
              <Skeleton />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState icon={<History />} title="Sin producción en el período" />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Producto</th>
                    <th style={{ textAlign: 'right' }}>Salió</th>
                    <th style={{ textAlign: 'right' }}>Rendimiento</th>
                    <th style={{ textAlign: 'right' }}>Costo</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} data-clickable="true" onClick={() => setOpen(r)} style={{ opacity: r.status === 'VOIDED' ? 0.5 : 1 }}>
                      <td className="bf-cell-sub">{formatDateTime(r.createdAt)}</td>
                      <td>
                        <span className="bf-cell-main">{r.outputProductName}</span> {r.status === 'VOIDED' && <Badge>Anulada</Badge>}
                        <div className="bf-cell-sub">
                          {r.recipeName ?? RECIPE_KIND_LABELS[r.kind]} · {r.branchName}
                        </div>
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {formatQty(r.outputUnitType, r.outputQty)}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {r.kind === 'CLEANING' && r.lossQty > 0 ? `merma ${formatQty('WEIGHT', r.lossQty)}` : pct(r.yieldRatio)}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {r.unitCostCents !== null ? `${formatMoney(r.unitCostCents)}${priceUnit(r.outputUnitType)}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {open && <RunDialog run={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function RunDialog({ run, onClose, justCreated }: { run: ProductionRunDTO; onClose: () => void; justCreated?: boolean }) {
  const qc = useQueryClient();
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const role = { INPUT: 'Usado', BYPRODUCT: 'Subproducto', FIXED_COST: 'Costo fijo' } as const;

  async function doVoid() {
    const parsed = voidReasonSchema.safeParse({ reason });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Contá el motivo.');
    setBusy(true);
    try {
      await api.post(`/production/runs/${run.id}/void`, parsed.data);
      await Promise.all(['production', 'stock', 'dashboard'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={justCreated ? `Listo: ${formatQty(run.outputUnitType, run.outputQty)} de ${run.outputProductName}` : run.outputProductName}
      description={`${run.recipeName ?? RECIPE_KIND_LABELS[run.kind]} · ${formatDateTime(run.createdAt)} · ${run.branchName}`}
      wide
    >
      <div className="bf-form">
        {run.status === 'VOIDED' && <Callout tone="danger">Anulada: {run.voidReason}</Callout>}
        {run.unitCostCents !== null && (
          <div className="bf-close-summary">
            <div>
              <span>Costo real</span>
              <strong className="bf-num">
                {formatMoney(run.unitCostCents)}
                {priceUnit(run.outputUnitType)}
              </strong>
            </div>
            <div>
              <span>{run.kind === 'CLEANING' ? 'Merma de limpieza' : 'Rendimiento'}</span>
              <strong className="bf-num">{run.kind === 'CLEANING' ? formatQty('WEIGHT', run.lossQty) : pct(run.yieldRatio)}</strong>
            </div>
            <div>
              <span>Subproductos</span>
              <strong className="bf-num">−{formatMoney(run.byproductValueCents ?? 0)}</strong>
            </div>
          </div>
        )}
        <div className="bf-scroll-box">
          <table className="bf-table">
            <tbody>
              {run.lines.map((l, i) => (
                <tr key={i}>
                  <td className="bf-cell-sub">{role[l.role]}</td>
                  <td className="bf-cell-main">{l.productName ?? l.label}</td>
                  <td className="bf-num">{l.unitType ? formatQty(l.unitType, l.qty) : ''}</td>
                  <td className="bf-num" style={{ textAlign: 'right' }}>
                    {l.costCents !== null ? formatMoney(l.costCents) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {run.expiresAt && <p className="bf-cell-sub">Vence el {formatDate(run.expiresAt)}.</p>}
        {voiding ? (
          <>
            <Field label="Motivo de la anulación" error={error ?? undefined}>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
            </Field>
            <div className="bf-form-actions">
              <Button onClick={() => setVoiding(false)} disabled={busy}>
                Volver
              </Button>
              <Button variant="danger" loading={busy} onClick={() => void doVoid()}>
                Anular: devolver todo al stock
              </Button>
            </div>
          </>
        ) : (
          <div className="bf-form-actions">
            {run.canVoid && (
              <Button variant="ghost" onClick={() => setVoiding(true)} style={{ marginRight: 'auto' }}>
                Anular
              </Button>
            )}
            {!run.canVoid && run.status === 'DONE' && (
              <span className="bf-cell-sub" style={{ marginRight: 'auto' }}>
                Ya se vendió o se usó parte de lo producido: no se puede anular.
              </span>
            )}
            <Button variant="primary" onClick={onClose}>
              Listo
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
